import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  ArrowLeft,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  FileCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { differenceInDays, isPast, differenceInMonths } from "date-fns";
import { SEO } from "@/components/seo/SEO";
import DocumentChecklistCard, { type DocumentStatus } from "@/components/shared/DocumentChecklistCard";
import { UnifiedDocumentLinkDialog } from "@/components/shared/UnifiedDocumentLinkDialog";
import { Badge } from "@/components/ui/badge";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";
import type { Tables } from "@/integrations/supabase/types";
import { useFamilyMembers, RELATIONSHIP_LABELS } from "@/hooks/useFamilyMembers";
import { 
  ESSENTIAL_DOCS_CONFIG, 
  CATEGORY_ORDER,
  type DocumentConfig,
  calculateAge,
  filterDocumentsByAge,
} from "@/config/essentialDocs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface UserDocument {
  id: string;
  title: string;
  doc_type: string;
  expires_at: string | null;
  file_name: string;
  file_path: string;
  keywords: string[] | null;
  created_at: string;
}

interface DocLink {
  id: string;
  category_key: string;
  document_id: string;
  document: UserDocument | null;
}

const FamilyMemberHealth = () => {
  const navigate = useNavigate();
  const { memberId } = useParams<{ memberId: string }>();
  const { openDocument } = useDocumentPreview();
  const { members, isLoading: membersLoading } = useFamilyMembers();
  
  const [docLinks, setDocLinks] = useState<DocLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedDocConfig, setSelectedDocConfig] = useState<DocumentConfig | null>(null);

  const member = members.find(m => m.id === memberId);
  const memberAge = member?.birth_date ? calculateAge(member.birth_date) : null;

  useEffect(() => {
    if (memberId) {
      fetchData();
    }
  }, [memberId]);

  const handleLinkSuccess = () => {
    setLinkDialogOpen(false);
    setSelectedDocConfig(null);
    fetchData();
  };

  const openLinkDialog = (docConfig: DocumentConfig) => {
    setSelectedDocConfig(docConfig);
    setLinkDialogOpen(true);
  };

  const fetchData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      const [docsResult, linksResult] = await Promise.all([
        supabase
          .from("documents")
          .select("id, title, doc_type, expires_at, file_name, file_path, keywords, created_at")
          .eq("user_id", user.id)
          .is("deleted_at", null)
          .order("created_at", { ascending: false }),
        supabase
          .from("essential_doc_links")
          .select("id, category_key, document_id")
          .eq("user_id", user.id)
          .eq("family_member_id", memberId)
      ]);

      if (docsResult.error) throw docsResult.error;
      if (linksResult.error) throw linksResult.error;

      const docs = docsResult.data || [];

      const links: DocLink[] = (linksResult.data || []).map(link => ({
        id: link.id,
        category_key: link.category_key,
        document_id: link.document_id,
        document: docs.find(d => d.id === link.document_id) as UserDocument || null
      }));
      setDocLinks(links);

    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les documents",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async (categoryKey: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from("essential_doc_links")
        .delete()
        .eq("user_id", user.id)
        .eq("family_member_id", memberId)
        .eq("category_key", categoryKey);

      if (error) throw error;

      toast({
        title: "Document délié",
        description: "Vous pouvez maintenant lier un autre document",
      });

      fetchData();
    } catch (error) {
      console.error("Error unlinking document:", error);
      toast({
        title: "Erreur",
        description: "Impossible de délier le document",
        variant: "destructive",
      });
    }
  };

  const getDocumentForCategory = (config: DocumentConfig) => {
    const link = docLinks.find(l => l.category_key === config.key);
    if (link?.document) {
      return { document: link.document, isLinked: true, linkId: link.id };
    }
    return { document: null, isLinked: false, linkId: null };
  };

  const getDocumentStatus = (doc: UserDocument | null, docConfig: DocumentConfig): { status: DocumentStatus; label: string; ageMonths?: number } => {
    if (!doc) {
      return { status: "missing", label: "Non lié" };
    }

    // Vérification spéciale pour les justificatifs < 3 mois
    if (docConfig.expiresShort) {
      const ageMonths = differenceInMonths(new Date(), new Date(doc.created_at));
      if (ageMonths >= 3) {
        return { status: "expired", label: `${ageMonths} mois`, ageMonths };
      }
      if (ageMonths >= 2) {
        return { status: "expiring", label: `${ageMonths} mois`, ageMonths };
      }
      return { status: "valid", label: "Récent", ageMonths };
    }

    if (!doc.expires_at) {
      return { status: "valid", label: "Lié" };
    }

    const expiresAt = new Date(doc.expires_at);
    const daysUntilExpiry = differenceInDays(expiresAt, new Date());

    if (isPast(expiresAt)) {
      return { status: "expired", label: "Expiré" };
    }

    if (daysUntilExpiry <= 30) {
      return { status: "expiring", label: `Expire dans ${daysUntilExpiry}j` };
    }

    return { status: "valid", label: "À jour" };
  };

  // Filtrer les documents pour ce membre de famille
  const getFilteredCategoryDocuments = (categoryKey: string): DocumentConfig[] => {
    const category = ESSENTIAL_DOCS_CONFIG[categoryKey];
    return filterDocumentsByAge(category.documents, memberAge);
  };

  // Vérifier si la catégorie est pertinente pour ce membre (enfant vs adulte)
  const isCategoryRelevant = (categoryKey: string): boolean => {
    // Certaines catégories peuvent être masquées pour les enfants
    if (memberAge !== null && memberAge < 16) {
      // Pas de Pro & Revenus pour les enfants < 16 ans
      if (categoryKey === "professional") return false;
    }
    
    const docs = getFilteredCategoryDocuments(categoryKey);
    return docs.length > 0;
  };

  const calculateGlobalScore = () => {
    let totalEssential = 0, validEssential = 0;

    CATEGORY_ORDER.forEach(categoryKey => {
      if (!isCategoryRelevant(categoryKey)) return;
      
      const docs = getFilteredCategoryDocuments(categoryKey);
      docs.forEach(docConfig => {
        if (!docConfig.isEssential) return;
        totalEssential++;
        const { document } = getDocumentForCategory(docConfig);
        const status = getDocumentStatus(document, docConfig);
        if (status.status === "valid") validEssential++;
      });
    });

    return totalEssential > 0 ? Math.round((validEssential / totalEssential) * 100) : 0;
  };

  const getGlobalStats = () => {
    let valid = 0, expiring = 0, expired = 0, missing = 0, totalEssential = 0;

    CATEGORY_ORDER.forEach(categoryKey => {
      if (!isCategoryRelevant(categoryKey)) return;
      
      const docs = getFilteredCategoryDocuments(categoryKey);
      docs.forEach(docConfig => {
        if (!docConfig.isEssential) return;
        totalEssential++;
        const { document } = getDocumentForCategory(docConfig);
        const status = getDocumentStatus(document, docConfig);
        
        switch (status.status) {
          case "valid": valid++; break;
          case "expiring": expiring++; break;
          case "expired": expired++; break;
          case "missing": missing++; break;
        }
      });
    });

    return { valid, expiring, expired, missing, total: totalEssential };
  };

  const getCategoryStats = (categoryKey: string) => {
    const docs = getFilteredCategoryDocuments(categoryKey);
    const essentialDocs = docs.filter(d => d.isEssential);
    let valid = 0, expiring = 0, expired = 0, missing = 0;
    const total = essentialDocs.length;

    essentialDocs.forEach(docConfig => {
      const { document } = getDocumentForCategory(docConfig);
      const status = getDocumentStatus(document, docConfig);
      
      switch (status.status) {
        case "valid": valid++; break;
        case "expiring": expiring++; break;
        case "expired": expired++; break;
        case "missing": missing++; break;
      }
    });

    return { valid, expiring, expired, missing, total };
  };

  const handleViewDocument = async (documentId: string) => {
    const { data: doc } = await supabase
      .from("documents")
      .select("*")
      .eq("id", documentId)
      .single();
    if (doc) openDocument(doc as Tables<"documents">);
  };

  const globalScore = calculateGlobalScore();
  const globalStats = getGlobalStats();

  if (loading || membersLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Membre non trouvé</p>
        <Button onClick={() => navigate("/family")}>Retour à la famille</Button>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`Documents de ${member.first_name} | EasyFinder`}
        description={`Gérez les documents de ${member.first_name} ${member.last_name}`}
      />

      <div className="min-h-screen bg-background md:pl-16">
        <div className="container max-w-4xl mx-auto px-4 py-6 pb-24 md:pb-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <FileCheck className="w-6 h-6 text-primary" />
                {member.first_name} {member.last_name}
              </h1>
              <p className="text-muted-foreground text-sm">
                {RELATIONSHIP_LABELS[member.relationship]}
                {memberAge !== null && ` · ${memberAge} ans`}
              </p>
            </div>
          </div>

          {/* Score Card */}
          <Card className="mb-6">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <svg className="w-16 h-16 -rotate-90">
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="6"
                        className="text-muted/20"
                      />
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="6"
                        strokeDasharray={`${(globalScore / 100) * 176} 176`}
                        strokeLinecap="round"
                        className={cn(
                          globalScore >= 80 ? "text-emerald-500" :
                          globalScore >= 50 ? "text-amber-500" : "text-destructive"
                        )}
                      />
                    </svg>
                    <span className={cn(
                      "absolute inset-0 flex items-center justify-center text-lg font-bold",
                      globalScore >= 80 ? "text-emerald-600" :
                      globalScore >= 50 ? "text-amber-600" : "text-destructive"
                    )}>
                      {globalScore}%
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">
                      Profil complété à {globalScore}%
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {globalStats.valid}/{globalStats.total} documents essentiels liés
                    </p>
                  </div>
                </div>

                {(globalStats.expiring + globalStats.expired + globalStats.missing) > 0 && (
                  <div className="flex gap-2 flex-wrap justify-end">
                    {globalStats.expired > 0 && (
                      <Badge variant="destructive" className="gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {globalStats.expired} expiré{globalStats.expired > 1 ? 's' : ''}
                      </Badge>
                    )}
                    {globalStats.expiring > 0 && (
                      <Badge variant="outline" className="gap-1 border-amber-500/50 text-amber-600">
                        {globalStats.expiring} bientôt
                      </Badge>
                    )}
                    {globalStats.missing > 0 && (
                      <Badge variant="outline" className="gap-1">
                        {globalStats.missing} manquant{globalStats.missing > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Categories Accordion */}
          <Accordion 
            type="multiple" 
            defaultValue={CATEGORY_ORDER.filter(k => isCategoryRelevant(k))} 
            className="space-y-3"
          >
            {CATEGORY_ORDER.map((categoryKey) => {
              if (!isCategoryRelevant(categoryKey)) return null;
              
              const category = ESSENTIAL_DOCS_CONFIG[categoryKey];
              const stats = getCategoryStats(categoryKey);
              const CategoryIcon = category.icon;
              const hasIssues = stats.expired + stats.expiring + stats.missing > 0;
              const allDocuments = getFilteredCategoryDocuments(categoryKey);

              return (
                <AccordionItem 
                  key={categoryKey} 
                  value={categoryKey}
                  className="border rounded-lg bg-card"
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline">
                    <div className="flex items-center justify-between w-full mr-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-muted">
                          <CategoryIcon className="w-5 h-5 text-foreground" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium">{category.label}</p>
                          <p className="text-sm text-muted-foreground">
                            {allDocuments.length} document{allDocuments.length > 1 ? 's' : ''}
                            {stats.total > 0 && ` • ${stats.valid}/${stats.total} essentiels OK`}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {hasIssues ? (
                          <>
                            {stats.expired > 0 && (
                              <Badge variant="destructive" className="gap-1">
                                {stats.expired}
                              </Badge>
                            )}
                            {stats.missing > 0 && (
                              <Badge variant="outline" className="gap-1">
                                {stats.missing}
                              </Badge>
                            )}
                          </>
                        ) : stats.total > 0 ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        ) : null}
                      </div>
                    </div>
                  </AccordionTrigger>
                  
                  <AccordionContent className="px-4 pb-4">
                    <div className="grid gap-3 md:grid-cols-2">
                      {allDocuments.map((docConfig) => {
                        const { document, isLinked } = getDocumentForCategory(docConfig);
                        const statusInfo = getDocumentStatus(document, docConfig);
                        
                        return (
                          <DocumentChecklistCard
                            key={docConfig.key}
                            typeKey={docConfig.key}
                            label={docConfig.label}
                            description={docConfig.description}
                            icon={CategoryIcon}
                            required={docConfig.isEssential}
                            status={statusInfo.status}
                            statusLabel={statusInfo.label}
                            linkedDocument={document ? {
                              id: document.id,
                              title: document.title,
                              file_name: document.file_name,
                              file_path: document.file_path,
                            } : null}
                            expiresAt={document?.expires_at}
                            onLink={() => openLinkDialog(docConfig)}
                            onView={handleViewDocument}
                            onUnlink={isLinked ? () => handleUnlink(docConfig.key) : undefined}
                            onRefresh={fetchData}
                            isEssential={docConfig.isEssential}
                            ageWarning={statusInfo.ageMonths !== undefined && statusInfo.ageMonths >= 2 ? { months: statusInfo.ageMonths } : undefined}
                          />
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          {globalScore < 100 && (
            <p className="text-sm text-muted-foreground text-center mt-6">
              Liez les documents essentiels pour compléter le profil de {member.first_name}.
            </p>
          )}
        </div>
      </div>

      {selectedDocConfig && (
        <UnifiedDocumentLinkDialog
          open={linkDialogOpen}
          onOpenChange={setLinkDialogOpen}
          entityType="health"
          entityId={memberId || ""}
          docConfig={{
            key: selectedDocConfig.key,
            label: selectedDocConfig.label,
            doc_type: selectedDocConfig.doc_type,
            keywords: selectedDocConfig.keywords,
          }}
          linkTableName="essential_doc_links"
          onSuccess={handleLinkSuccess}
          showValidityDates={true}
          familyMemberId={memberId}
        />
      )}
    </>
  );
};

export default FamilyMemberHealth;
